import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import { Avatar, Button, Center, Flex, Icon, Box } from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationLink } from '../../components/NotificationLink';
import { NotificationTime } from '../../components/Time';
import { AutoClosedNoteBanner } from '../../components/AutoClosedNoteBanner';
import { KText } from '@/ui-library/components/text';
import { Eye } from '@kula-ai/icons/outline/Eye';
import {
  ratingOptions,
  getSelectionRatingStyle,
} from '@/constants/scorecard-configs';
import { find } from 'lodash-es';
import {
  Hackerrank,
  Hackerearth,
  CriteriaCorp,
  Hireflix,
} from '@/assets/icons/ats';
import type { ReviewAssessmentScorecardProps } from './ReviewAssessmentScorecrad.interface';

const ReviewAssessmentScorecard = ({
  notification = {},
  onClose = () => {},
}: ReviewAssessmentScorecardProps) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes = '', id: notificationId } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const {
    reviewer,
    candidate,
    job,
    assessment,
    application_id,
    scorecard,
    auto_closed_note,
  } = data || {};
  const scorecardRating = scorecard?.rating || {};

  const {
    id: candidateId,
    avatar_url: candidateAvatarUrl,
    name: candidateName,
  } = candidate;
  const { avatar_url: reviewerAvatarUrl, name: reviewerName } = reviewer;
  // todo check if id can be passed
  const { name: jobName, url: jobUrl } = job;
  const {
    id: assessmentId,
    name: assessmentName,
    platform: assessmentPlatform,
  } = assessment || {};

  const getPlatformConfig = (platform) => {
    switch (platform) {
      case 'hackerrank':
        return {
          bg: 'black',
          icon: Hackerrank,
        };
      case 'hackerearth':
        return {
          bg: 'black',
          icon: Hackerearth,
        };
      case 'criteriacorp':
        return {
          bg: '#09264E',
          icon: CriteriaCorp,
          iconProps: {
            width: '18px',
            height: '18px',
          },
        };
      case 'hireflix':
        return {
          bg: '#5862FF',
          icon: Hireflix,
          iconProps: {
            width: '18px',
            height: '18px',
          },
        };
      default:
        return {
          bg: 'ats-tertiary',
          icon: Eye,
        };
    }
  };

  const selectedRating = find(ratingOptions, {
    value: scorecardRating,
  });

  const styleProps = getSelectionRatingStyle(selectedRating);
  const label = selectedRating?.label;
  const IconComponent = selectedRating?.icon;
  const platformConfig = getPlatformConfig(assessmentPlatform);

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="46px" h="46px" borderRadius="full" bg={platformConfig.bg}>
        <Icon
          as={platformConfig.icon}
          color="ats-primary"
          {...platformConfig.iconProps}
        />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        {label ? (
          <KText size="sm-regular">
            <KText as="span" size="sm-semi-bold">
              {assessmentName}
            </KText>{' '}
            feedback submitted:{' '}
            <KText as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${candidateId}`}
                label={candidateName}
                onClose={onClose}
                notification={notification}
              />
            </KText>{' '}
            -{' '}
            <KText as="span" size="sm-semi-bold">
              {jobName}
            </KText>
          </KText>
        ) : (
          <KText>
            <KText as="span" size="sm-regular">
              {`${reviewerName} has finished evaluating `}
            </KText>
            <KText as="span" size="sm-semi-bold">
              <NotificationLink
                to={`/contacts/${candidateId}`}
                label={`${candidateName}'s`}
                onClose={onClose}
                notification={notification}
              />
            </KText>
            <KText as="span" size="sm-regular">
              {' '}
              assessment
            </KText>
          </KText>
        )}

        <KText size="sm-semi-bold">Details</KText>
        <Flex
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          p="14px"
          flexDir="column"
          gap="6px"
          mt="-6px"
        >
          {typeof auto_closed_note === 'string' && auto_closed_note.trim() ? (
            <AutoClosedNoteBanner message={auto_closed_note} />
          ) : null}
          <KText size="sm-semi-bold">Candidate</KText>
          <Flex mb="6px" gap="6px">
            <Avatar
              size="2xs"
              borderRadius="4px"
              className="avatar-xs"
              src={candidateAvatarUrl}
              name={candidateName}
            />
            <KText size="sm-regular">{candidateName}</KText>
          </Flex>
          <KText size="sm-semi-bold">Reviewer</KText>
          <Flex mb="6px" gap="6px">
            <Avatar
              size="2xs"
              borderRadius="4px"
              className="avatar-xs"
              src={reviewerAvatarUrl}
              name={reviewerName}
            />
            <KText size="sm-regular">{reviewerName}</KText>
          </Flex>
          {selectedRating ? (
            <Box py="2">
              <KText size="sm-semi-bold">Overall evaluation</KText>
              <Flex gap="6px">
                {IconComponent ? (
                  <Icon
                    as={IconComponent}
                    h={styleProps.h || '24px'}
                    w={styleProps.w || '24px'}
                    color={styleProps?.color}
                  />
                ) : null}
                <KText size="sm-regular">{label}</KText>
              </Flex>
            </Box>
          ) : null}
          <KText size="sm-semi-bold">Job</KText>
          <KText size="sm-regular" mb="6px">
            {jobName}
          </KText>
          <KText size="sm-semi-bold">Assessment</KText>
          <KText size="sm-regular" mb="6px">
            {assessmentName}
          </KText>
        </Flex>
        <NotificationTime notification={notification} />
        <Button
          w="fit-content"
          variant="primary"
          borderRadius="8px"
          onClick={() => {
            markAsRead({ id: notificationId });
            window.open(
              `${jobUrl}?tab=candidates&appId=${application_id}&reviewAssessment=${assessmentId}`,
              '_self'
            );
            onClose();
          }}
          maxH="34px"
          data-test-id="view-scorecard-button"
        >
          View scorecard now
        </Button>
      </Flex>
    </Flex>
  );
};

export const ReviewAssessmentScorecardNotification = ({
  notification = {},
  onClose = () => {},
}: ReviewAssessmentScorecardProps) => {
  return (
    <NotificationCard notification={notification}>
      <ReviewAssessmentScorecard
        notification={notification}
        onClose={onClose}
      />
    </NotificationCard>
  );
};
